import * as RadixTabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface TabItem {
  value: string;
  label: string;
  /** Mono count right of the label. */
  count?: number | string;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Panels keyed by tab value. Omit to drive content yourself. */
  children?: ReactNode;
  /** Compact padding (px-3 py-2). */
  compact?: boolean;
  'aria-label'?: string;
  className?: string;
  listClassName?: string;
}

/** Radix Tabs with the inset 2px active bar (inset-x-3, shorter than the tab). Arrow keys move focus. */
export function Tabs({ items, value, defaultValue, onValueChange, children, compact = false, className, listClassName, ...aria }: TabsProps) {
  return (
    <RadixTabs.Root value={value} defaultValue={defaultValue ?? items[0]?.value} onValueChange={onValueChange} className={className}>
      <RadixTabs.List aria-label={aria['aria-label']} className={cn('flex items-center border-b border-line', listClassName)}>
        {items.map((t) => (
          <RadixTabs.Trigger
            key={t.value}
            value={t.value}
            disabled={t.disabled}
            className={cn(
              'relative shrink-0 text-nav text-mute transition-colors hover:text-ink focus-visible:outline-offset-[-1px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=active]:text-ink',
              compact ? 'px-3 py-2' : 'px-4 py-2.5',
              'group',
            )}
          >
            {t.label}
            {t.count !== undefined ? <span className="ml-1.5 font-mono text-2xs text-mute">{t.count}</span> : null}
            <span aria-hidden className={cn('absolute bottom-0 h-[2px] bg-brand opacity-0 group-data-[state=active]:opacity-100', compact ? 'inset-x-2' : 'inset-x-3')} />
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {children}
    </RadixTabs.Root>
  );
}

export interface TabPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ value, children, className }: TabPanelProps) {
  return (
    <RadixTabs.Content value={value} className={cn('outline-none focus-visible:outline-1', className)}>
      {children}
    </RadixTabs.Content>
  );
}
